import { apiRequest } from "@common/api";
import { getDeploymentConfig } from "@common/deployment-config";
import { fetchWithLogging, type FetchOptions } from "@common/fetch-logger";

// A save state is a few hundred KB to a few MB through the API, so its
// bytes get longer than a JSON call's 8 s.
const SAVE_BYTES: FetchOptions = { timeoutMs: 60_000 };

// play-api's save routes:
//   GET    /saves                 -> CloudSaveMeta[]
//   GET    /saves/{name}          -> application/octet-stream
//   PUT    /saves/{name}          <- application/octet-stream
//   DELETE /saves/{name}
// Names are mGBA's own (<base>.sav, <base>.ss1..3); saves are scoped to the
// caller's sub server-side.

export interface CloudSaveMeta {
  name: string;
  size: number;
  updatedAt: string;
}

/**
 * The account's cloud save names with the token it is given (at load it may
 * still be in the URL hash), or null when the cloud can't say.
 */
export async function cloudSaveNames(token: string): Promise<Set<string> | null> {
  try {
    const response = await fetchWithLogging(
      `${getDeploymentConfig().apiUrl}/saves`,
      { headers: { Authorization: `Bearer ${token}` } },
      { timeoutMs: 4_000 },
    );
    if (!response.ok) return null;
    return new Set(((await response.json()) as CloudSaveMeta[]).map((s) => s.name));
  } catch {
    return null;
  }
}

export const cloudSaves = {
  // `signal` cancels the request: leaving the player gives up on a slow sync.
  async list(signal?: AbortSignal): Promise<CloudSaveMeta[]> {
    return (await apiRequest("/saves", { signal })).json();
  },

  async upload(name: string, bytes: Uint8Array, signal?: AbortSignal): Promise<void> {
    await apiRequest(
      `/saves/${encodeURIComponent(name)}`,
      {
        method: "PUT",
        signal,
        headers: { "Content-Type": "application/octet-stream" },
        body: bytes as BodyInit,
      },
      SAVE_BYTES,
    );
  },

  async download(name: string, signal?: AbortSignal): Promise<Uint8Array> {
    const response = await apiRequest(`/saves/${encodeURIComponent(name)}`, { signal }, SAVE_BYTES);
    return new Uint8Array(await response.arrayBuffer());
  },

  async remove(name: string, signal?: AbortSignal): Promise<void> {
    await apiRequest(`/saves/${encodeURIComponent(name)}`, { method: "DELETE", signal });
  },
};
